'use client';
import React, { useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Image from 'next/image';
import {
  ArrowLeft, Clock, Star, CheckCircle, AlertCircle, Lock, X,
  ArrowUpRight, TrendingUp, Shield, Users, FileText, Calendar,
  DollarSign, BarChart2, Globe, Building2, ChevronRight, Info,
} from 'lucide-react';
import CampaignRatingSection from './CampaignRatingSection';
import ProjectDirectorSection from './ProjectDirectorSection';

// ─── Types ────────────────────────────────────────────────────────────────────

type RiskLevel = 'Low' | 'Medium' | 'High';
type EligibilityStatus = 'eligible' | 'not-eligible' | 'pending-kyc' | 'amount-restricted';

interface Campaign {
  id: string;
  name: string;
  issuer: string;
  category: string;
  riskLevel: RiskLevel;
  target: number;
  raised: number;
  funded: number;
  minInvestment: number;
  irr: string;
  tenor: string;
  daysLeft: number;
  eligibility: EligibilityStatus;
  eligibilityNote?: string;
  image: string;
  imageAlt: string;
  imageTint: string;
  featured?: boolean;
  jurisdiction: string;
  // Extended detail fields
  description: string;
  highlights: string[];
  useOfProceeds: { label: string; pct: number; color: string }[];
  keyMetrics: { label: string; value: string; sub?: string }[];
  documents: { name: string; type: string; size: string; date: string }[];
  timeline: { label: string; date: string; status: 'completed' | 'active' | 'upcoming' }[];
  issuerProfile: { founded: string; employees: string; hq: string; rating: string; website: string };
  investorCount: number;
  closingDate: string;
  regulatoryRef: string;
}

// ─── Mock Data (shared with discovery) ───────────────────────────────────────

const allCampaigns: Campaign[] = [
  {
    id: 'camp-001',
    name: 'Meridian Logistics Hub — Series B',
    issuer: 'Meridian Infrastructure PLC',
    category: 'Infrastructure',
    riskLevel: 'Medium',
    target: 12500000,
    raised: 9875000,
    funded: 79,
    minInvestment: 50000,
    irr: '14.2%',
    tenor: '5 yrs',
    daysLeft: 23,
    eligibility: 'eligible',
    jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Meridian Logistics Hub — modern warehouse and logistics facility in Jebel Ali Free Zone',
    imageTint: 'from-blue-900/60 to-blue-700/20',
    featured: true,
    description:
      'Meridian Logistics Hub Series B is a senior secured infrastructure debt offering financing the expansion of a 280,000 sqm Class-A logistics campus in Jebel Ali Free Zone. The facility serves blue-chip e-commerce and 3PL tenants under long-term triple-net leases, providing predictable cash flows backed by hard assets in one of the world\'s busiest trade corridors.',
    highlights: [
      '280,000 sqm Class-A logistics campus in JAFZA',
      '100% pre-leased to investment-grade tenants',
      'Senior secured first-lien debt with 1.4× DSCR covenant',
      'Quarterly coupon distributions from Day 1',
      'DIFC-regulated SPV with independent trustee oversight',
    ],
    useOfProceeds: [
      { label: 'Construction & Fit-Out', pct: 62, color: 'bg-primary' },
      { label: 'Land Acquisition', pct: 22, color: 'bg-accent' },
      { label: 'Working Capital Reserve', pct: 10, color: 'bg-positive' },
      { label: 'Fees & Expenses', pct: 6, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '14.2%', sub: 'Net of fees' },
      { label: 'Tenor', value: '5 Years', sub: 'Bullet maturity' },
      { label: 'Coupon', value: '11.5% p.a.', sub: 'Quarterly pay' },
      { label: 'LTV Ratio', value: '58%', sub: 'At origination' },
      { label: 'DSCR', value: '1.4×', sub: 'Minimum covenant' },
      { label: 'Credit Rating', value: 'BBB+', sub: 'Internal rating' },
    ],
    documents: [
      { name: 'Information Memorandum', type: 'PDF', size: '4.2 MB', date: '2026-07-15' },
      { name: 'Financial Model (Base Case)', type: 'XLSX', size: '1.8 MB', date: '2026-07-15' },
      { name: 'Legal Opinion — DIFC', type: 'PDF', size: '890 KB', date: '2026-07-20' },
      { name: 'Independent Valuation Report', type: 'PDF', size: '2.1 MB', date: '2026-07-18' },
      { name: 'Subscription Agreement', type: 'PDF', size: '560 KB', date: '2026-07-22' },
    ],
    timeline: [
      { label: 'Offering Launch', date: '2026-06-01', status: 'completed' },
      { label: 'Soft Close (75%)', date: '2026-07-28', status: 'completed' },
      { label: 'Hard Close', date: '2026-08-31', status: 'active' },
      { label: 'First Drawdown', date: '2026-09-15', status: 'upcoming' },
      { label: 'Construction Start', date: '2026-10-01', status: 'upcoming' },
      { label: 'First Coupon Payment', date: '2026-12-15', status: 'upcoming' },
    ],
    issuerProfile: {
      founded: '2014',
      employees: '340+',
      hq: 'Dubai, UAE',
      rating: 'A- (Fitch)',
      website: 'meridianinfra.ae',
    },
    investorCount: 87,
    closingDate: '31 Aug 2026',
    regulatoryRef: 'DIFC-CL-2026-0441',
  },
  {
    id: 'camp-002',
    name: 'Ashford Commercial REIT II',
    issuer: 'Ashford Capital Partners',
    category: 'Real Estate',
    riskLevel: 'Low',
    target: 25000000,
    raised: 14200000,
    funded: 57,
    minInvestment: 100000,
    irr: '11.8%',
    tenor: '7 yrs',
    daysLeft: 41,
    eligibility: 'eligible',
    jurisdiction: 'UAE (ADGM)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Ashford Commercial REIT II — premium commercial property portfolio across GCC',
    imageTint: 'from-amber-900/60 to-amber-700/20',
    description:
      'Ashford Commercial REIT II acquires and manages a diversified portfolio of Grade-A office towers and mixed-use commercial assets across Abu Dhabi, Dubai, and Riyadh. The fund targets stable income from long-term government and corporate leases, with capital appreciation driven by GCC urbanisation and Vision 2030 infrastructure spend.',
    highlights: [
      'Diversified GCC commercial portfolio — 6 Grade-A assets',
      '78% occupancy with WALE of 5.2 years',
      'Government & quasi-government tenants represent 45% of NRI',
      'Semi-annual distributions with 8% preferred return',
      'ADGM-regulated REIT with Big-4 audited financials',
    ],
    useOfProceeds: [
      { label: 'Asset Acquisitions', pct: 70, color: 'bg-primary' },
      { label: 'Capex & Refurbishment', pct: 18, color: 'bg-accent' },
      { label: 'Liquidity Reserve', pct: 8, color: 'bg-positive' },
      { label: 'Structuring Costs', pct: 4, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '11.8%', sub: 'Net of fees' },
      { label: 'Tenor', value: '7 Years', sub: 'With extension option' },
      { label: 'Preferred Return', value: '8% p.a.', sub: 'Semi-annual' },
      { label: 'Portfolio WALE', value: '5.2 yrs', sub: 'Weighted avg lease' },
      { label: 'Occupancy', value: '78%', sub: 'Current' },
      { label: 'AUM', value: '$142M', sub: 'Total portfolio' },
    ],
    documents: [
      { name: 'Prospectus', type: 'PDF', size: '6.1 MB', date: '2026-06-10' },
      { name: 'Portfolio Valuation Report', type: 'PDF', size: '3.4 MB', date: '2026-06-08' },
      { name: 'Audited Financials FY2025', type: 'PDF', size: '2.2 MB', date: '2026-05-30' },
      { name: 'Subscription Agreement', type: 'PDF', size: '620 KB', date: '2026-06-12' },
    ],
    timeline: [
      { label: 'Offering Launch', date: '2026-05-15', status: 'completed' },
      { label: '50% Milestone', date: '2026-07-10', status: 'completed' },
      { label: 'Hard Close', date: '2026-09-15', status: 'active' },
      { label: 'First Asset Settlement', date: '2026-10-01', status: 'upcoming' },
      { label: 'First Distribution', date: '2027-01-15', status: 'upcoming' },
    ],
    issuerProfile: { founded: '2009', employees: '210+', hq: 'Abu Dhabi, UAE', rating: 'A (S&P)', website: 'ashfordcapital.ae' },
    investorCount: 134,
    closingDate: '15 Sep 2026',
    regulatoryRef: 'ADGM-FSRA-2026-0188',
  },
  {
    id: 'camp-003',
    name: 'ClearPath Clean Energy Fund III',
    issuer: 'ClearPath Renewables Ltd',
    category: 'Clean Energy',
    riskLevel: 'Low',
    target: 8000000,
    raised: 7840000,
    funded: 98,
    minInvestment: 25000,
    irr: '12.6%',
    tenor: '8 yrs',
    daysLeft: 7,
    eligibility: 'not-eligible',
    eligibilityNote: 'Fund nearly full — below minimum allocation threshold',
    jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'ClearPath Clean Energy Fund III — solar and wind renewable energy infrastructure',
    imageTint: 'from-green-900/60 to-green-700/20',
    description:
      'ClearPath Clean Energy Fund III finances utility-scale solar PV and onshore wind projects across the MENA region. The fund holds long-term power purchase agreements with sovereign-backed offtakers, delivering stable, inflation-linked cash flows while contributing to regional net-zero commitments.',
    highlights: [
      '4 operational solar & wind assets — 380 MW combined capacity',
      'PPAs with sovereign-backed offtakers (20-year terms)',
      'Carbon credit revenue provides additional yield uplift',
      'Green bond framework aligned with ICMA principles',
      'Fund 98% subscribed — final allocation window',
    ],
    useOfProceeds: [
      { label: 'Project Equity', pct: 75, color: 'bg-positive' },
      { label: 'Debt Service Reserve', pct: 15, color: 'bg-primary' },
      { label: 'O&M Reserve', pct: 7, color: 'bg-accent' },
      { label: 'Fees', pct: 3, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '12.6%', sub: 'Net of fees' },
      { label: 'Tenor', value: '8 Years', sub: 'With 2yr extension' },
      { label: 'Capacity', value: '380 MW', sub: 'Combined installed' },
      { label: 'PPA Term', value: '20 Years', sub: 'Avg remaining' },
      { label: 'CO₂ Offset', value: '420K t/yr', sub: 'Estimated annual' },
      { label: 'Funded', value: '98%', sub: 'Near full close' },
    ],
    documents: [
      { name: 'Fund Prospectus', type: 'PDF', size: '5.0 MB', date: '2025-12-01' },
      { name: 'Technical Due Diligence', type: 'PDF', size: '3.8 MB', date: '2025-11-28' },
      { name: 'Green Bond Framework', type: 'PDF', size: '1.2 MB', date: '2025-12-05' },
    ],
    timeline: [
      { label: 'Fund Launch', date: '2026-01-10', status: 'completed' },
      { label: '90% Subscribed', date: '2026-06-01', status: 'completed' },
      { label: 'Final Close', date: '2026-08-29', status: 'active' },
      { label: 'Final Drawdown', date: '2026-09-10', status: 'upcoming' },
    ],
    issuerProfile: { founded: '2017', employees: '95+', hq: 'Dubai, UAE', rating: 'BBB+ (Fitch)', website: 'clearpath-re.com' },
    investorCount: 212,
    closingDate: '29 Aug 2026',
    regulatoryRef: 'DIFC-CL-2026-0312',
  },
  {
    id: 'camp-004',
    name: 'Vantage Private Credit Fund I',
    issuer: 'Vantage Capital Management',
    category: 'Debt / Credit',
    riskLevel: 'Medium',
    target: 15000000,
    raised: 4500000,
    funded: 30,
    minInvestment: 75000,
    irr: '9.4%',
    tenor: '3 yrs',
    daysLeft: 58,
    eligibility: 'eligible',
    jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Vantage Private Credit Fund I — institutional private credit and SME lending portfolio',
    imageTint: 'from-purple-900/60 to-purple-700/20',
    description:
      'Vantage Private Credit Fund I provides senior secured direct lending to established GCC SMEs with revenues of $5M–$50M. The fund targets underserved mid-market borrowers with strong cash flows but limited access to traditional bank financing, offering investors a floating-rate return with downside protection through first-lien security packages.',
    highlights: [
      'Senior secured first-lien lending to GCC SMEs',
      'Floating rate: SOFR + 650bps (floor 9.4%)',
      'Diversified across 25–30 borrowers at full deployment',
      'Average loan-to-value of 52% with personal guarantees',
      'Monthly NAV reporting with quarterly liquidity windows',
    ],
    useOfProceeds: [
      { label: 'Loan Origination', pct: 85, color: 'bg-purple-500' },
      { label: 'Liquidity Buffer', pct: 10, color: 'bg-primary' },
      { label: 'Fees & Expenses', pct: 5, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '9.4%', sub: 'Floor rate' },
      { label: 'Tenor', value: '3 Years', sub: 'Evergreen option' },
      { label: 'Rate', value: 'SOFR+650', sub: 'Floating' },
      { label: 'Avg LTV', value: '52%', sub: 'At origination' },
      { label: 'Borrowers', value: '25–30', sub: 'At full deployment' },
      { label: 'Liquidity', value: 'Quarterly', sub: 'Redemption windows' },
    ],
    documents: [
      { name: 'Private Placement Memorandum', type: 'PDF', size: '4.5 MB', date: '2026-06-20' },
      { name: 'Credit Policy & Underwriting', type: 'PDF', size: '1.6 MB', date: '2026-06-20' },
      { name: 'Subscription Agreement', type: 'PDF', size: '540 KB', date: '2026-06-22' },
    ],
    timeline: [
      { label: 'Fund Launch', date: '2026-06-25', status: 'completed' },
      { label: 'First Deployment', date: '2026-08-01', status: 'completed' },
      { label: 'Hard Close', date: '2026-10-20', status: 'active' },
      { label: 'Full Deployment', date: '2026-12-01', status: 'upcoming' },
      { label: 'First Quarterly Window', date: '2027-03-31', status: 'upcoming' },
    ],
    issuerProfile: { founded: '2019', employees: '60+', hq: 'Dubai, UAE', rating: 'BB+ (Internal)', website: 'vantagecap.ae' },
    investorCount: 42,
    closingDate: '20 Oct 2026',
    regulatoryRef: 'DIFC-CL-2026-0509',
  },
  {
    id: 'camp-005',
    name: 'Solaris Renewable Energy Fund III',
    issuer: 'Solaris Asset Management DIFC',
    category: 'Clean Energy',
    riskLevel: 'Medium',
    target: 25000000,
    raised: 0,
    funded: 0,
    minInvestment: 50000,
    irr: '13.1%',
    tenor: '7 yrs',
    daysLeft: 72,
    eligibility: 'pending-kyc',
    eligibilityNote: 'Annual suitability re-assessment required',
    jurisdiction: 'UAE (ADGM)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Solaris Renewable Energy Fund III — utility-scale solar projects across MENA region',
    imageTint: 'from-yellow-900/60 to-yellow-700/20',
    description:
      'Solaris Renewable Energy Fund III targets utility-scale solar PV projects across Egypt, Saudi Arabia, and Jordan, capitalising on rapidly falling LCOE and strong government renewable mandates. The fund employs a build-operate-transfer model, generating returns through construction uplift, operational cash flows, and strategic asset sales.',
    highlights: [
      'Build-operate-transfer model across 3 MENA markets',
      'Pipeline of 6 projects totalling 520 MW',
      'Offtake secured with sovereign-backed utilities',
      'Construction risk mitigated by EPC wrap with liquidated damages',
      'Aligned with Saudi Vision 2030 and Egypt\'s 42% renewables target',
    ],
    useOfProceeds: [
      { label: 'Project Development', pct: 68, color: 'bg-yellow-500' },
      { label: 'Construction Reserve', pct: 20, color: 'bg-primary' },
      { label: 'Debt Service Reserve', pct: 8, color: 'bg-accent' },
      { label: 'Fees', pct: 4, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '13.1%', sub: 'Net of fees' },
      { label: 'Tenor', value: '7 Years', sub: 'With extension' },
      { label: 'Pipeline', value: '520 MW', sub: '6 projects' },
      { label: 'Markets', value: '3', sub: 'EG, SA, JO' },
      { label: 'LCOE', value: '$18/MWh', sub: 'Blended estimate' },
      { label: 'Status', value: 'Pre-launch', sub: 'KYC required' },
    ],
    documents: [
      { name: 'Teaser / Executive Summary', type: 'PDF', size: '2.1 MB', date: '2026-07-01' },
      { name: 'Pipeline Overview', type: 'PDF', size: '3.3 MB', date: '2026-07-01' },
    ],
    timeline: [
      { label: 'Offering Launch', date: '2026-08-22', status: 'active' },
      { label: 'First Close (40%)', date: '2026-10-15', status: 'upcoming' },
      { label: 'Hard Close', date: '2026-11-03', status: 'upcoming' },
      { label: 'First Project Drawdown', date: '2026-12-01', status: 'upcoming' },
    ],
    issuerProfile: { founded: '2015', employees: '180+', hq: 'Abu Dhabi, UAE', rating: 'BBB (Moody\'s)', website: 'solarisam.ae' },
    investorCount: 0,
    closingDate: '03 Nov 2026',
    regulatoryRef: 'ADGM-FSRA-2026-0271',
  },
  {
    id: 'camp-006',
    name: 'Harborview Private Credit Fund',
    issuer: 'Harborview Capital DIFC',
    category: 'Debt / Credit',
    riskLevel: 'High',
    target: 40000000,
    raised: 0,
    funded: 0,
    minInvestment: 100000,
    irr: '15.5%',
    tenor: '5 yrs',
    daysLeft: 90,
    eligibility: 'amount-restricted',
    eligibilityNote: 'Min. investment $100K — exceeds your current allocation limit',
    jurisdiction: 'UAE (DIFC)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Harborview Private Credit Fund — senior secured SME lending across GCC markets',
    imageTint: 'from-red-900/60 to-red-700/20',
    description:
      'Harborview Private Credit Fund provides senior secured and mezzanine financing to high-growth GCC SMEs and mid-market companies in the $50M–$500M revenue bracket. The fund targets higher-yielding opportunities with enhanced security packages, offering sophisticated investors access to institutional-grade private credit with attractive risk-adjusted returns.',
    highlights: [
      'Senior secured + mezzanine tranche structure',
      'Target 25–35 borrowers across GCC and Levant',
      'Enhanced security: first-lien, share pledge, personal guarantee',
      'Active portfolio monitoring with quarterly covenant testing',
      'GP co-investment of $5M aligning manager incentives',
    ],
    useOfProceeds: [
      { label: 'Senior Secured Loans', pct: 60, color: 'bg-red-500' },
      { label: 'Mezzanine Lending', pct: 28, color: 'bg-primary' },
      { label: 'Liquidity Reserve', pct: 8, color: 'bg-accent' },
      { label: 'Fees', pct: 4, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '15.5%', sub: 'Net of fees' },
      { label: 'Tenor', value: '5 Years', sub: 'Closed-end' },
      { label: 'Mezzanine', value: '28%', sub: 'Of portfolio' },
      { label: 'GP Co-invest', value: '$5M', sub: 'Aligned' },
      { label: 'Borrowers', value: '25–35', sub: 'Target' },
      { label: 'Risk', value: 'High', sub: 'Sophisticated only' },
    ],
    documents: [
      { name: 'Private Placement Memorandum', type: 'PDF', size: '5.2 MB', date: '2026-07-30' },
      { name: 'Risk Disclosure Statement', type: 'PDF', size: '980 KB', date: '2026-07-30' },
      { name: 'Subscription Agreement', type: 'PDF', size: '610 KB', date: '2026-08-01' },
    ],
    timeline: [
      { label: 'Offering Launch', date: '2026-08-22', status: 'active' },
      { label: 'First Close', date: '2026-10-31', status: 'upcoming' },
      { label: 'Hard Close', date: '2026-11-21', status: 'upcoming' },
      { label: 'First Deployment', date: '2026-12-15', status: 'upcoming' },
    ],
    issuerProfile: { founded: '2016', employees: '75+', hq: 'Dubai, UAE', rating: 'BB (Internal)', website: 'harborviewcap.ae' },
    investorCount: 0,
    closingDate: '21 Nov 2026',
    regulatoryRef: 'DIFC-CL-2026-0588',
  },
  {
    id: 'camp-007',
    name: 'Nexus PropTech Growth Capital',
    issuer: 'Nexus Ventures MENA',
    category: 'Technology',
    riskLevel: 'High',
    target: 5000000,
    raised: 1200000,
    funded: 24,
    minInvestment: 5000,
    irr: '18.0%',
    tenor: '3 yrs',
    daysLeft: 45,
    eligibility: 'eligible',
    jurisdiction: 'UAE (Mainland)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'Nexus PropTech Growth Capital — B2B PropTech SaaS platform for GCC real estate developers',
    imageTint: 'from-cyan-900/60 to-cyan-700/20',
    description:
      'Nexus PropTech Growth Capital finances the Series B expansion of a B2B SaaS platform serving GCC real estate developers with AI-powered project management, sales CRM, and regulatory compliance tools. The platform has 120+ developer clients, $4.2M ARR, and is growing at 85% YoY, targeting a regional market of $1.2B.',
    highlights: [
      '120+ developer clients — $4.2M ARR growing 85% YoY',
      'AI-powered project management & compliance automation',
      'Expansion into Saudi Arabia, Qatar, and Egypt',
      'Strategic partnership with 3 of top 5 UAE developers',
      'Convertible note structure with 20% discount on Series C',
    ],
    useOfProceeds: [
      { label: 'Product & Engineering', pct: 45, color: 'bg-cyan-500' },
      { label: 'Sales & Marketing', pct: 30, color: 'bg-primary' },
      { label: 'Market Expansion', pct: 18, color: 'bg-accent' },
      { label: 'G&A', pct: 7, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '18.0%', sub: 'Equity upside' },
      { label: 'Tenor', value: '3 Years', sub: 'Convertible note' },
      { label: 'ARR', value: '$4.2M', sub: '85% YoY growth' },
      { label: 'Clients', value: '120+', sub: 'Active developers' },
      { label: 'Market', value: '$1.2B', sub: 'GCC TAM' },
      { label: 'Series C Discount', value: '20%', sub: 'On conversion' },
    ],
    documents: [
      { name: 'Investor Deck', type: 'PDF', size: '3.1 MB', date: '2026-07-05' },
      { name: 'Financial Projections', type: 'XLSX', size: '1.4 MB', date: '2026-07-05' },
      { name: 'Term Sheet', type: 'PDF', size: '420 KB', date: '2026-07-08' },
    ],
    timeline: [
      { label: 'Round Opens', date: '2026-07-10', status: 'completed' },
      { label: '25% Milestone', date: '2026-08-15', status: 'active' },
      { label: 'Hard Close', date: '2026-10-06', status: 'upcoming' },
      { label: 'Funds Deployed', date: '2026-10-20', status: 'upcoming' },
    ],
    issuerProfile: { founded: '2021', employees: '45+', hq: 'Dubai, UAE', rating: 'N/A (Startup)', website: 'nexusventures.ae' },
    investorCount: 28,
    closingDate: '06 Oct 2026',
    regulatoryRef: 'MAINLAND-DED-2026-0774',
  },
  {
    id: 'camp-008',
    name: 'GCC Healthcare REIT I',
    issuer: 'MedCapital Partners',
    category: 'Healthcare',
    riskLevel: 'Low',
    target: 18000000,
    raised: 9000000,
    funded: 50,
    minInvestment: 30000,
    irr: '10.2%',
    tenor: '10 yrs',
    daysLeft: 35,
    eligibility: 'eligible',
    jurisdiction: 'UAE (ADGM)',
    image: '/assets/images/image-1787244773683.png',
    imageAlt: 'GCC Healthcare REIT I — diversified healthcare real estate investment trust across Gulf region',
    imageTint: 'from-teal-900/60 to-teal-700/20',
    description:
      'GCC Healthcare REIT I acquires and manages a diversified portfolio of healthcare real estate assets including hospitals, specialist clinics, and medical office buildings across the UAE, Saudi Arabia, and Kuwait. The fund benefits from structural tailwinds including ageing populations, rising chronic disease prevalence, and government healthcare privatisation programmes.',
    highlights: [
      'Diversified healthcare portfolio — hospitals, clinics, MOBs',
      'Triple-net leases with healthcare operators (avg 12-yr WALE)',
      'Government privatisation pipeline provides acquisition flow',
      'Inflation-linked rent escalators (CPI + 1.5%)',
      'ADGM-regulated REIT with quarterly distributions',
    ],
    useOfProceeds: [
      { label: 'Asset Acquisitions', pct: 72, color: 'bg-teal-500' },
      { label: 'Capex & Fit-Out', pct: 16, color: 'bg-primary' },
      { label: 'Liquidity Reserve', pct: 8, color: 'bg-accent' },
      { label: 'Fees', pct: 4, color: 'bg-warning' },
    ],
    keyMetrics: [
      { label: 'Target IRR', value: '10.2%', sub: 'Net of fees' },
      { label: 'Tenor', value: '10 Years', sub: 'Closed-end REIT' },
      { label: 'WALE', value: '12 Years', sub: 'Avg lease term' },
      { label: 'Rent Escalator', value: 'CPI+1.5%', sub: 'Annual' },
      { label: 'Distribution', value: 'Quarterly', sub: '8% preferred' },
      { label: 'Markets', value: '3', sub: 'UAE, SA, KW' },
    ],
    documents: [
      { name: 'REIT Prospectus', type: 'PDF', size: '5.8 MB', date: '2026-06-15' },
      { name: 'Portfolio Asset Schedule', type: 'PDF', size: '2.4 MB', date: '2026-06-15' },
      { name: 'Audited Financials FY2025', type: 'PDF', size: '1.9 MB', date: '2026-05-28' },
      { name: 'Subscription Agreement', type: 'PDF', size: '590 KB', date: '2026-06-18' },
    ],
    timeline: [
      { label: 'REIT Launch', date: '2026-06-20', status: 'completed' },
      { label: '50% Subscribed', date: '2026-08-10', status: 'completed' },
      { label: 'Hard Close', date: '2026-09-26', status: 'active' },
      { label: 'First Asset Settlement', date: '2026-10-15', status: 'upcoming' },
      { label: 'First Distribution', date: '2026-12-31', status: 'upcoming' },
    ],
    issuerProfile: { founded: '2012', employees: '155+', hq: 'Abu Dhabi, UAE', rating: 'A- (Moody\'s)', website: 'medcapital.ae' },
    investorCount: 98,
    closingDate: '26 Sep 2026',
    regulatoryRef: 'ADGM-FSRA-2026-0302',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const riskConfig: Record<RiskLevel, { color: string; bg: string }> = {
  Low: { color: 'text-positive', bg: 'bg-positive/10 border-positive/20' },
  Medium: { color: 'text-warning', bg: 'bg-warning/10 border-warning/20' },
  High: { color: 'text-critical', bg: 'bg-critical/10 border-critical/20' },
};

const eligibilityConfig: Record<EligibilityStatus, { icon: React.ElementType; color: string; label: string; canInvest: boolean }> = {
  eligible: { icon: CheckCircle, color: 'text-positive', label: 'Eligible to Invest', canInvest: true },
  'not-eligible': { icon: X, color: 'text-critical', label: 'Not Eligible', canInvest: false },
  'pending-kyc': { icon: AlertCircle, color: 'text-warning', label: 'KYC Required', canInvest: false },
  'amount-restricted': { icon: Lock, color: 'text-warning', label: 'Amount Restricted', canInvest: false },
};

function FundingBar({ funded }: { funded: number }) {
  const fillColor = funded >= 90
    ? 'bg-gradient-to-r from-positive to-accent'
    : funded >= 50
    ? 'bg-gradient-to-r from-primary to-accent' :'bg-gradient-to-r from-surface-4 to-primary';
  return (
    <div className="w-full h-2 bg-surface-4 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${fillColor}`} style={{ width: `${funded}%` }} />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CampaignDetailView() {
  const params = useParams();
  const router = useRouter();
  const id = params?.id as string;

  const campaign = useMemo(() => allCampaigns.find((c) => c.id === id), [id]);

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle size={40} className="text-muted-foreground" />
        <p className="text-lg font-semibold text-foreground">Campaign not found</p>
        <p className="text-sm text-muted-foreground">The campaign you are looking for does not exist or has been removed.</p>
        <button onClick={() => router.push('/campaign-discovery')} className="btn-primary text-sm gap-2 mt-2">
          <ArrowLeft size={14} /> Back to Discovery
        </button>
      </div>
    );
  }

  const eligCfg = eligibilityConfig[campaign.eligibility];
  const riskCfg = riskConfig[campaign.riskLevel];
  const EligIcon = eligCfg.icon;

  return (
    <div className="space-y-6 pb-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <button onClick={() => router.push('/campaign-discovery')} className="hover:text-foreground transition-colors flex items-center gap-1">
          <ArrowLeft size={12} /> Campaign Discovery
        </button>
        <ChevronRight size={12} />
        <span className="text-foreground font-medium truncate max-w-[300px]">{campaign.name}</span>
      </div>

      {/* Hero Section */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="relative h-56 w-full">
          <Image src={campaign.image} alt={campaign.imageAlt} fill className="object-cover" sizes="100vw" />
          <div className={`absolute inset-0 bg-gradient-to-br ${campaign.imageTint}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

          {/* Top badges */}
          <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs bg-black/50 backdrop-blur-sm text-white px-2.5 py-1 rounded-full font-medium">{campaign.category}</span>
              {campaign.featured && (
                <span className="text-xs bg-primary/80 text-white px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                  <Star size={10} /> Featured
                </span>
              )}
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${riskCfg.bg} ${riskCfg.color}`}>
              {campaign.riskLevel} Risk
            </span>
          </div>

          {/* Bottom info */}
          <div className="absolute bottom-4 left-4 right-4">
            <h1 className="text-xl font-bold text-white leading-tight mb-1">{campaign.name}</h1>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-white/80 flex items-center gap-1"><Building2 size={12} />{campaign.issuer}</span>
              <span className="text-sm text-white/80 flex items-center gap-1"><Globe size={12} />{campaign.jurisdiction}</span>
              <span className="text-sm text-white/80 flex items-center gap-1"><Calendar size={12} />Closes {campaign.closingDate}</span>
            </div>
          </div>
        </div>

        {/* Eligibility + CTA bar */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-between gap-4 flex-wrap">
          <div className={`flex items-center gap-2 text-sm font-medium ${eligCfg.color}`}>
            <EligIcon size={15} />
            {eligCfg.label}
            {campaign.eligibilityNote && (
              <span className="text-xs text-muted-foreground font-normal ml-1">— {campaign.eligibilityNote}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-surface-3 px-3 py-1.5 rounded-lg">
              <Clock size={12} />
              <span className="font-mono-data font-semibold text-foreground">{campaign.daysLeft}</span> days left
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-surface-3 px-3 py-1.5 rounded-lg">
              <Users size={12} />
              <span className="font-mono-data font-semibold text-foreground">{campaign.investorCount}</span> investors
            </div>
            {eligCfg.canInvest ? (
              <button className="btn-primary text-sm gap-1.5"><ArrowUpRight size={14} /> Invest Now</button>
            ) : (
              <button className="btn-secondary text-sm gap-1.5 opacity-50 cursor-not-allowed" disabled>
                <Lock size={14} /> Restricted
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left column — 2/3 */}
        <div className="xl:col-span-2 space-y-6">

          {/* Funding Progress */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><TrendingUp size={15} className="text-primary" /> Funding Progress</h2>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Amount Raised</span>
                <span className="font-mono-data font-bold text-foreground">{campaign.funded}% funded</span>
              </div>
              <FundingBar funded={campaign.funded} />
              <div className="flex justify-between text-xs mt-2 text-muted-foreground">
                <span className="font-mono-data">${(campaign.raised / 1_000_000).toFixed(2)}M raised</span>
                <span className="font-mono-data">Target: ${(campaign.target / 1_000_000).toFixed(1)}M</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 pt-1">
              <div className="bg-surface-3 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                <p className="font-mono-data font-bold text-foreground text-sm">${((campaign.target - campaign.raised) / 1_000_000).toFixed(2)}M</p>
              </div>
              <div className="bg-surface-3 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Min. Investment</p>
                <p className="font-mono-data font-bold text-foreground text-sm">${campaign.minInvestment >= 1000 ? `${campaign.minInvestment / 1000}K` : campaign.minInvestment}</p>
              </div>
              <div className="bg-surface-3 rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Days Left</p>
                <p className="font-mono-data font-bold text-foreground text-sm">{campaign.daysLeft}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="glass-card rounded-xl p-5 space-y-3">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Info size={15} className="text-primary" /> Overview</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{campaign.description}</p>
            <div className="pt-1 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Key Highlights</p>
              <ul className="space-y-1.5">
                {campaign.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle size={13} className="text-positive flex-shrink-0 mt-0.5" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><BarChart2 size={15} className="text-primary" /> Key Metrics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {campaign.keyMetrics.map((m) => (
                <div key={m.label} className="bg-surface-3 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{m.label}</p>
                  <p className="font-mono-data font-bold text-foreground text-base mt-0.5">{m.value}</p>
                  {m.sub && <p className="text-xs text-muted-foreground/70 mt-0.5">{m.sub}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Use of Proceeds */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><DollarSign size={15} className="text-primary" /> Use of Proceeds</h2>
            <div className="space-y-3">
              {campaign.useOfProceeds.map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-mono-data font-semibold text-foreground">{item.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-surface-4 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><FileText size={15} className="text-primary" /> Documents</h2>
            <div className="space-y-2">
              {campaign.documents.map((doc) => (
                <div key={doc.name} className="flex items-center justify-between p-3 bg-surface-3 rounded-lg hover:bg-surface-4 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText size={14} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">{doc.type} · {doc.size} · {doc.date}</p>
                    </div>
                  </div>
                  <ArrowUpRight size={14} className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>

          {/* Project Director */}
          <ProjectDirectorSection campaignId={campaign.id} />

          {/* Ratings & Reviews */}
          <CampaignRatingSection campaignId={campaign.id} campaignName={campaign.name} />
        </div>

        {/* Right column — 1/3 */}
        <div className="space-y-6">

          {/* Issuer Profile */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Building2 size={15} className="text-primary" /> Issuer Profile</h2>
            <div className="space-y-3">
              {[
                { label: 'Issuer', value: campaign.issuer },
                { label: 'Founded', value: campaign.issuerProfile.founded },
                { label: 'Employees', value: campaign.issuerProfile.employees },
                { label: 'Headquarters', value: campaign.issuerProfile.hq },
                { label: 'Credit Rating', value: campaign.issuerProfile.rating },
                { label: 'Website', value: campaign.issuerProfile.website },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-start gap-2">
                  <span className="text-xs text-muted-foreground flex-shrink-0">{row.label}</span>
                  <span className="text-xs font-medium text-foreground text-right">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Risk & Compliance */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Shield size={15} className="text-primary" /> Risk & Compliance</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Risk Level</span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${riskCfg.bg} ${riskCfg.color}`}>{campaign.riskLevel}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Jurisdiction</span>
                <span className="text-xs font-medium text-foreground">{campaign.jurisdiction}</span>
              </div>
              <div className="flex justify-between items-start gap-2">
                <span className="text-xs text-muted-foreground flex-shrink-0">Regulatory Ref</span>
                <span className="text-xs font-mono-data text-foreground text-right">{campaign.regulatoryRef}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Your Eligibility</span>
                <span className={`text-xs font-medium flex items-center gap-1 ${eligCfg.color}`}>
                  <EligIcon size={11} /> {eligCfg.label}
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="glass-card rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Calendar size={15} className="text-primary" /> Campaign Timeline</h2>
            <div className="space-y-0">
              {campaign.timeline.map((item, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${
                      item.status === 'completed' ? 'bg-positive' :
                      item.status === 'active'? 'bg-primary ring-2 ring-primary/30' : 'bg-surface-4 border border-border'
                    }`} />
                    {i < campaign.timeline.length - 1 && (
                      <div className={`w-px flex-1 my-1 ${item.status === 'completed' ? 'bg-positive/30' : 'bg-border'}`} style={{ minHeight: '20px' }} />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className={`text-xs font-medium ${item.status === 'upcoming' ? 'text-muted-foreground' : 'text-foreground'}`}>{item.label}</p>
                    <p className="text-xs text-muted-foreground/70 font-mono-data">{item.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Invest CTA */}
          <div className="glass-card rounded-xl p-5 space-y-3">
            <div className="text-center space-y-1">
              <p className="text-sm font-semibold text-foreground">Ready to invest?</p>
              <p className="text-xs text-muted-foreground">Min. investment: <span className="font-mono-data font-semibold text-foreground">${campaign.minInvestment >= 1000 ? `${campaign.minInvestment / 1000}K` : campaign.minInvestment}</span></p>
            </div>
            {eligCfg.canInvest ? (
              <button className="btn-primary w-full text-sm gap-2"><ArrowUpRight size={14} /> Commit Investment</button>
            ) : (
              <button className="btn-secondary w-full text-sm gap-2 opacity-50 cursor-not-allowed" disabled>
                <Lock size={14} /> {eligCfg.label}
              </button>
            )}
            <button onClick={() => router.push('/campaign-discovery')} className="btn-ghost w-full text-xs gap-1.5 text-muted-foreground">
              <ArrowLeft size={12} /> Back to Discovery
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
